import React, { useEffect, useState } from 'react';
import { Card, Spin, Alert, Typography, Descriptions, Tag, Space, Empty } from 'antd';
import dayjs from 'dayjs';
import apiClient from '@/utils/apiClient';
import { ICancelReasonItem } from '@/interfaces/IPMetrics';

const { Title } = Typography;

interface ChurnDetailProps {
	identifier: string;
}

const ChurnDetail: React.FC<ChurnDetailProps> = ({ identifier }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [churnData, setChurnData] = useState<ICancelReasonItem[]>([]);

	useEffect(() => {
		if (!identifier) {
			setError('No identifier provided.');
			setLoading(false);
			return;
		}

		const fetchChurnDetail = async () => {
			setLoading(true);
			try {
				const response = await apiClient.request({
					endPoint: `/api/admin/cancel-reasons/${encodeURIComponent(identifier)}`,
					method: 'GET',
					clientType: 'ADMIN',
				});
				setChurnData(response.data);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load churn details.');
			} finally {
				setLoading(false);
			}
		};

		fetchChurnDetail();
	}, [identifier]);

	return (
		<Space direction="vertical" size="large" style={{ width: '100%' }}>
			<Card>
				<Title level={4}>Churn History for: {identifier}</Title>
				<Spin spinning={loading}>
					{error && <Alert message="Error" description={error} type="error" showIcon />}

					{!loading && !error && churnData.length === 0 && (
						<Empty description="No churn records found for this identifier." />
					)}

					{!error &&
						churnData.map((record, index) => (
							<Card
								key={index}
								type="inner"
								title={`Churn Event on: ${dayjs(record.date).format('MMM D, YYYY HH:mm')}`}
								style={{ marginTop: index > 0 ? '16px' : '0' }}
							>
								<Descriptions bordered column={1} size="small">
									<Descriptions.Item label="Reason Provided">
										{record.cancel_reason || 'N/A'}
									</Descriptions.Item>

									{record.llm_analysis && (
										<>
											<Descriptions.Item label="LLM Category">
												<Tag>{record.llm_analysis.category}</Tag>
											</Descriptions.Item>
											<Descriptions.Item label="LLM Subcategory">
												{record.llm_analysis.subcategory}
											</Descriptions.Item>
											<Descriptions.Item label="Sentiment">
												<Tag
													color={
														record.llm_analysis.sentiment === 'positive'
															? 'green'
															: record.llm_analysis.sentiment === 'negative'
																? 'red'
																: 'orange'
													}
												>
													{record.llm_analysis.sentiment}
												</Tag>
											</Descriptions.Item>
										</>
									)}

									{record.stripe_cancel_reasons && (
										<>
											<Descriptions.Item label="Stripe Feedback">
												{record.stripe_cancel_reasons.feedback || 'N/A'}
											</Descriptions.Item>
											<Descriptions.Item label="Stripe Comment">
												{record.stripe_cancel_reasons.comment || 'N/A'}
											</Descriptions.Item>
										</>
									)}
								</Descriptions>
							</Card>
						))}
				</Spin>
			</Card>
		</Space>
	);
};

export default ChurnDetail;
